import Drawer from "components/drawerSlide/drawer";
import FormLayout from "components/form/FormLayout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "components/icon/Icon";
import { Select } from "components/select/index";
import SelectNew from "components/select/SelectNew";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import moment, { MomentInput } from "moment";
import { IDivision } from "redux/reducers/models/facility";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { IBookingCategory } from "redux/reducers/models/teetime";
import ReactDOM from "react-dom";
import { useSearch } from "hooks/useSearch/useSearch";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import "../TeeSheetTemplate.scss";
import TimePick from "components/timePick/TimePick";
import { TShotgunTemplate } from "redux/reducers/models/teesheet";
import { GetShotGunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/template";
import { showError } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import BookingCategoryTagsSelect from "./BookingCategoryTagsSelect";

export interface ITeeTimeSettingShotgunState {
    startTime: string;
    frontDivisionId: number;
    backDivisionId: number;
    duration: string;
    leadTime: string;
    bookingCategoryIds: Array<number>;
    tournamentId: number;
    leagueId: number;
    shotgunTemplateId: number;
}

interface IProps {
    open: boolean;
    bookingCategories: Array<IBookingCategory>;
    divisions: Array<IDivision>;
    onOk: (teeTimeSettings: ITeeTimeSettingShotgunState) => void;
    onClose: () => void;
    subtitle?: string;
}

export default function ShotgunDrawer(props: IProps) {
    const { open, onOk, onClose, bookingCategories, divisions, subtitle } = props;
    const { Option } = Select;
    const dispatch = useAppDispatch();
    const leaguesHook = useSearch<ILeague>({
        fetchAll: () => GetLeague(null, false).then(res => res.data),
        searchFn: search =>
          GetLeague({ search }, false).then(res => res.data),
      });
      
      const tournamentsHook = useSearch<ITournament>({
        fetchAll: () => GetTournament(null, false).then(res => res.data),
        searchFn: search =>
          GetTournament({ search }, false).then(res => res.data),
      });

      const leagues = leaguesHook.displayItems;
      const tournaments = tournamentsHook.displayItems;


    const [state, setState] = useState<ITeeTimeSettingShotgunState>({
        startTime: "",
        frontDivisionId: null,
        backDivisionId: null,
        duration: "",
        leadTime: "",
        bookingCategoryIds: [],
        tournamentId: null,
        leagueId: null,
        shotgunTemplateId: null,
    });

    const [shotgunTemplates, setShotgunTemplates] = useState<Array<TShotgunTemplate>>([]);

    const [eventState, setEventState] = useState<{selectedTournament: ITournament; selectedLeague: ILeague;}>({
        selectedLeague: null,
        selectedTournament: null,
    });
    const {t} = useTranslation();


    useEffect(() => {
        void loadShotgunTemplates();
    }, []);

    async function loadShotgunTemplates() {
        const shotgunTemplateRes = await GetShotGunTemplate(null, false);
        if (shotgunTemplateRes?.status !== StatusCode.OK) {
            dispatch(showError(t("Error getting shotgun templates")));
            return;
          }
        setShotgunTemplates(shotgunTemplateRes?.data ?? []);
    }

      function clearDrawer() {
        ReactDOM.unstable_batchedUpdates(() => {
          setState(prev => ({
            ...prev,
            startTime: "",
            frontDivisionId: null,
            backDivisionId: null,
            duration: "",
            leadTime: "",
            bookingCategoryIds: [],
            tournamentId: null,
            leagueId: null,
            shotgunTemplateId: null,
          }));
          setEventState(prevState => ({
            ...prevState,
            selectedTournament: null,
            selectedLeague: null,
          }));
        });
      }

      function handleClose() {
        void clearDrawer();
        void onClose();
      }

      function handleOk() {
        if (!state?.startTime) {
            dispatch(showError(t("Start time required")));
            return;
          } else if (!state?.duration) {
            dispatch(showError(t("Duration required")));
            return;
          } else if (!state?.leadTime) {
            dispatch(showError(t("Lead time required")));
            return;
          } else if (!state?.frontDivisionId) {
            dispatch(showError(t("Front division required")));
            return;
          } else if (!state?.backDivisionId) {
            dispatch(showError(t("Back division required")));
            return;
          } else if (!state?.shotgunTemplateId) {
            dispatch(showError(t("Template required")));
            return;
          }

        void clearDrawer();
        void onOk(state);
      }


    return (
        <Drawer
        open={open}
        title={t("Apply settings")}
        subtitle={subtitle}
        onClose={handleClose}
        onOk={handleOk}
        okText={t("Create")}
        backDropClose={true}
        hideBackdrop
        showCancel={true}
        cancelText="Cancel"
        cancelButtonType="text"
        secondaryActions={[{label: t("Clear all"), action: () => clearDrawer()}]}
        // okDisabled={}
      >

        <Drawer.Section bordered>
          <FormLayout>
            <FormLayout.Group>
              <TimePick label={t("Start time")} value={state?.startTime} onChange={time => setState(prev => ({...prev, startTime: time}))} size="medium"/>
            </FormLayout.Group>
            <FormLayout.Group>
            <SelectNew
              id="front_division"
              label={t("Front")}
              value={state.frontDivisionId}
              options={divisions?.map(division => {
              return {
                  id: division.id,
                  label: division.title,
              };
              })}
              onChange={value => setState(prev => ({...prev, frontDivisionId: value}))}
              placeholder={t("Select division")}
              // hightLightSelected
          />
          <SelectNew
              id="back_division"
              label={t("Back")}
              value={state.backDivisionId}
              options={divisions?.map(division => {
              return {
                  id: division.id,
                  label: division.title,
              };
              })}
              onChange={value => setState(prev => ({...prev, backDivisionId: value}))}
              placeholder={t("Select division")}
              // hightLightSelected
          />
            </FormLayout.Group>
            <FormLayout.Group>
              <TimePick label={t("Duration")} value={state?.duration} onChange={time => setState(prev => ({...prev, duration: time}))} size="medium" hideMeridiem={12}/>
              <TimePick label={t("Lead time")} value={state?.leadTime} onChange={time => setState(prev => ({...prev, leadTime: time}))} size="medium" hideMeridiem={12} />
            </FormLayout.Group>
            <FormLayout.Group>
              <BookingCategoryTagsSelect
                bookingCategories={bookingCategories}
                selectedIds={state.bookingCategoryIds}
                onChange={bookingCategoryIds => setState(prev => ({ ...prev, bookingCategoryIds }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
                <SelectNew
                    id="shotgun_templates"
                    label={t("Template")}
                    value={state.shotgunTemplateId}
                    options={shotgunTemplates?.map(template => {
                    return {
                        id: template.id,
                        label: template.title,
                    };
                    })}
                    onChange={value => setState(prev => ({...prev, shotgunTemplateId: value}))}
                    placeholder={t("Select a template")}
                    // hightLightSelected
                />
            </FormLayout.Group>
          </FormLayout>
        </Drawer.Section>
        <Drawer.Section bordered>
          <h1>{t("Nest an event")} - {t("optional")}</h1>
          <br />
          <FormLayout>
            <FormLayout.Group>
            {eventState.selectedTournament ? (
                    <div>
                      <div>
                        <p className="event-label">{t('Tournament')}</p>
                      </div>

                      <div className="selected-container">
                        <div className="event-name">
                          <div>{eventState?.selectedTournament?.name}</div>

                          <div>
                            <button className="" onClick={() => {
                              ReactDOM.unstable_batchedUpdates(() => {
                                setState(prevState => ({ ...prevState, tournamentId: null }));
                                setEventState(prevState => ({
                                  ...prevState,
                                  selectedTournament: null,
                                }));
                              });
                            }}>
                              <Icon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Select
                        showSearch
                        label={t("Tournament")}
                        onSearch={(value: string) => tournamentsHook.setSearchValue(value)}
                        onChange={(_, tournament: ITournament) => {
                          ReactDOM.unstable_batchedUpdates(() => {
                            setState(prevState => ({ ...prevState, tournamentId: tournament.id }));
                            setEventState(prevState => ({ ...prevState, selectedTournament: tournament }));
                          });
                        }
                        }
                        placeholder={t("Search tournament...")}
                        allowClear
                        searchValue={tournamentsHook.searchValue}
                        showDropDownOnFocus={true}
                        searching={tournamentsHook.loading}
                        disabled={!!state?.leagueId}
                      >
                        <Option
                          value={"none"}
                          extraValues={{
                            name: t("None"),
                            id: "none",
                          }}
                        >
                          <div className="">{t("None")}</div>
                        </Option>

                        {tournaments?.filter(tournament => !moment(tournament.date).isBefore(moment(), "day"))
                          .map((tournament: any, index: number) => {
                            return (
                              <Option key={index} value={tournament.id} extraValues={tournament}>
                                <div className="flex items-center">
                                  <span>{tournament.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(tournament.date as MomentInput).format("LL")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}

                        <Option disabled value={-2} key={-2} name={""}>
                          <div>
                            <hr style={{ margin: "0px" }} />
                          </div>
                        </Option>

                        {tournaments?.filter(tournament => moment(tournament.date).isBefore(moment(), "day"))
                          .map((tournament: any, index: number) => {
                            return (
                              <Option key={index} value={tournament.id} extraValues={tournament}>
                                <div className="flex items-center">
                                  <span>{tournament.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(tournament.date as MomentInput).format("LL")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}
                      </Select>
                    </div>
                  )}
            </FormLayout.Group>
            <FormLayout.Group>
            {eventState?.selectedLeague ? (
                    <div>
                      <div>
                        <p className="event-label">{t('League')}</p>
                      </div>

                      <div className="selected-container">
                        <div className="event-name">
                          <div>{eventState?.selectedLeague?.name}</div>

                          <div>
                            <button className="" onClick={() => {
                              ReactDOM.unstable_batchedUpdates(() => {
                                setState(prevState => ({ ...prevState, leagueId: null }));
                                setEventState(prevState => ({
                                  ...prevState,
                                  selectedLeague: null,
                                }));
                              });
                            }}>
                              <Icon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Select
                        showSearch
                        label={t("League")}
                        onSearch={(value: string) => leaguesHook.setSearchValue(value)}
                        onChange={(_, league: ILeague) => {
                          ReactDOM.unstable_batchedUpdates(() => {
                            setState(prevState => ({ ...prevState, leagueId: league.id }));
                            setEventState(prevState => ({ ...prevState, selectedLeague: league }));
                          });
                        }}
                        placeholder={t("Search league...")}
                        allowClear
                        searchValue={leaguesHook.searchValue}
                        showDropDownOnFocus={true}
                        searching={leaguesHook.loading}
                        disabled={!!state?.tournamentId}
                      >
                        <Option
                          value={"none"}
                          extraValues={{
                            name: t("None"),
                            id: "none",
                          }}
                        >
                          <div className="">{t("None")}</div>
                        </Option>
                        {leagues?.filter(league => league.start_date && !moment(league.start_date).isBefore(moment(), "day"))
                          ?.map((league: any, index: number) => {
                            return (
                              <Option value={league?.id} key={index} extraValues={league}>
                                <div className="">
                                  <span className="">{league.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(league.start_date as MomentInput).format("YYYY")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}

                        <Option disabled value={-2} key={-2} name={""}>
                          <div>
                            <hr style={{ margin: "0px" }} />
                          </div>
                        </Option>

                        {leagues?.filter(league => !league.start_date || moment(league.start_date).isBefore(moment(), "day"))
                          ?.map((league: any, index: number) => {
                            return (
                              <Option value={league?.id} key={index} extraValues={league}>
                                <div className="">
                                  <span className="">{league.name}</span>
                                  {league?.start_date && (
                                    <span className="shotgun-tournament-date">
                                      {moment(league.start_date as MomentInput).format("YYYY")}
                                    </span>
                                  )}
                                </div>
                              </Option>
                            );
                          })}
                      </Select>
                    </div>
                  )}
            </FormLayout.Group>
          </FormLayout>
        </Drawer.Section>
      </Drawer>
    );
}